import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  Pagination,
  Stack,
} from '@mui/material'
import { Add, Edit, Delete, Refresh } from '@mui/icons-material'
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore'
import { db } from '../../firebase'

interface Coupon {
  id: string
  code: string
  description: string
  discountType: 'percentage' | 'fixedAmount'
  discountValue: number
  expiryDate: Date
  maxUsagePerUser?: number
  maxTotalUsage?: number
  currentUsage: number
  isActive: boolean
  minimumOrderAmount?: number
  applicableCategories?: string[]
  createdAt: Date
  updatedAt: Date
}

const CouponsPage: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(12)
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discountType: 'percentage' as 'percentage' | 'fixedAmount',
    discountValue: 0,
    expiryDate: '',
    maxUsagePerUser: '',
    maxTotalUsage: '',
    minimumOrderAmount: '',
    isActive: true,
  })

  useEffect(() => {
    loadCoupons()
  }, [])

  const loadCoupons = async () => {
    try {
      setLoading(true)
      const querySnapshot = await getDocs(collection(db, 'coupons'))
      const couponsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        expiryDate: doc.data().expiryDate?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as Coupon[]
      
      setCoupons(couponsData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()))
    } catch (error) {
      console.error('Error loading coupons:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateCoupon = async () => {
    try {
      const couponData = {
        code: formData.code.toUpperCase(),
        description: formData.description,
        discountType: formData.discountType,
        discountValue: Number(formData.discountValue),
        expiryDate: Timestamp.fromDate(new Date(formData.expiryDate)),
        maxUsagePerUser: formData.maxUsagePerUser ? Number(formData.maxUsagePerUser) : null,
        maxTotalUsage: formData.maxTotalUsage ? Number(formData.maxTotalUsage) : null,
        minimumOrderAmount: formData.minimumOrderAmount ? Number(formData.minimumOrderAmount) : null,
        currentUsage: 0,
        isActive: formData.isActive,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      }

      if (editingCoupon) {
        await updateDoc(doc(db, 'coupons', editingCoupon.id), {
          ...couponData,
          updatedAt: Timestamp.now(),
        })
      } else {
        await addDoc(collection(db, 'coupons'), couponData)
      }

      setDialogOpen(false)
      resetForm()
      loadCoupons()
    } catch (error) {
      console.error('Error saving coupon:', error)
    }
  }

  const handleDeleteCoupon = async (couponId: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الكوبون؟')) {
      try {
        await deleteDoc(doc(db, 'coupons', couponId))
        loadCoupons()
      } catch (error) {
        console.error('Error deleting coupon:', error)
      }
    }
  }

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discountType: 'percentage',
      discountValue: 0,
      expiryDate: '',
      maxUsagePerUser: '',
      maxTotalUsage: '',
      minimumOrderAmount: '',
      isActive: true,
    })
    setEditingCoupon(null)
  }

  const openEditDialog = (coupon: Coupon) => {
    setEditingCoupon(coupon)
    setFormData({
      code: coupon.code,
      description: coupon.description,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      expiryDate: coupon.expiryDate.toISOString().split('T')[0],
      maxUsagePerUser: coupon.maxUsagePerUser?.toString() || '',
      maxTotalUsage: coupon.maxTotalUsage?.toString() || '',
      minimumOrderAmount: coupon.minimumOrderAmount?.toString() || '',
      isActive: coupon.isActive,
    })
    setDialogOpen(true)
  }

  const openCreateDialog = () => {
    resetForm()
    setDialogOpen(true)
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ar-EG')
  }

  const isExpired = (expiryDate: Date) => {
    return expiryDate < new Date()
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          إدارة الكوبونات
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadCoupons}
            sx={{ mr: 1 }}
          >
            تحديث
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openCreateDialog}
          >
            إنشاء كوبون جديد
          </Button>
        </Box>
      </Box>

      {coupons.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="textSecondary">
                لا توجد كوبونات
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                انقر على "إنشاء كوبون جديد" لإضافة كوبونات خصم
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={2}>
            {coupons.slice((page - 1) * pageSize, page * pageSize).map((coupon) => (
            <Grid item xs={12} md={6} lg={4} key={coupon.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="h2" color="primary">
                      {coupon.code}
                    </Typography>
                    <Box>
                      <Chip
                        label={coupon.isActive ? 'نشط' : 'غير نشط'}
                        color={coupon.isActive ? 'success' : 'default'}
                        size="small"
                      />
                      {isExpired(coupon.expiryDate) && (
                        <Chip
                          label="منتهي"
                          color="error"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {coupon.description}
                  </Typography>

                  <Box mb={2}>
                    <Typography variant="body2">
                      <strong>نوع الخصم:</strong> {coupon.discountType === 'percentage' ? 'نسبة مئوية' : 'مبلغ ثابت'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>قيمة الخصم:</strong> {coupon.discountValue}{coupon.discountType === 'percentage' ? '%' : ' جنيه'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>الاستخدام:</strong> {coupon.currentUsage}{coupon.maxTotalUsage ? ` / ${coupon.maxTotalUsage}` : ''}
                    </Typography>
                    <Typography variant="body2">
                      <strong>انتهاء الصلاحية:</strong> {formatDate(coupon.expiryDate)}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(coupon)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCoupon(coupon.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          </Grid>
          {Math.ceil(coupons.length / pageSize) > 1 && (
            <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
              <Pagination
                count={Math.ceil(coupons.length / pageSize)}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
                size="large"
              />
            </Stack>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCoupon ? 'تعديل الكوبون' : 'إنشاء كوبون جديد'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="كود الكوبون"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="الحد الأدنى للطلب (جنيه)"
                  type="number"
                  value={formData.minimumOrderAmount}
                  onChange={(e) => setFormData({ ...formData, minimumOrderAmount: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="وصف الكوبون"
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <FormLabel>نوع الخصم</FormLabel>
                  <RadioGroup
                    value={formData.discountType}
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value as 'percentage' | 'fixedAmount' })}
                    row
                  >
                    <FormControlLabel value="percentage" control={<Radio />} label="نسبة مئوية" />
                    <FormControlLabel value="fixedAmount" control={<Radio />} label="مبلغ ثابت" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={formData.discountType === 'percentage' ? 'نسبة الخصم (%)' : 'مبلغ الخصم (جنيه)'}
                  type="number"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: Number(e.target.value) })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="تاريخ انتهاء الصلاحية"
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="الحد الأقصى للاستخدام لكل مستخدم"
                  type="number"
                  value={formData.maxUsagePerUser}
                  onChange={(e) => setFormData({ ...formData, maxUsagePerUser: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="الحد الأقصى للاستخدام الإجمالي"
                  type="number"
                  value={formData.maxTotalUsage}
                  onChange={(e) => setFormData({ ...formData, maxTotalUsage: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <FormLabel>حالة الكوبون</FormLabel>
                  <RadioGroup
                    value={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                    row
                  >
                    <FormControlLabel value={true} control={<Radio />} label="نشط" />
                    <FormControlLabel value={false} control={<Radio />} label="غير نشط" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>إلغاء</Button>
          <Button onClick={handleCreateCoupon} variant="contained">
            {editingCoupon ? 'حفظ التعديلات' : 'إنشاء الكوبون'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CouponsPage

